import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import {
  Upload, Search, Plus, Edit, Trash2, RefreshCw, Package, FileSpreadsheet,
  CheckCircle2, X, AlertTriangle, Download,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeader = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const emptyDraft = () => ({
  hs_description: "",
  brand_name: "",
  generic_name: "",
  product_code: "",
  hs_code: "",
  htsus_code: "",
  description: "",
});

const EDITABLE = ["hs_description", "generic_name", "product_code", "hs_code", "htsus_code"];

const EditableCell = ({ row, field, editing, onStart, onSave, onCancel, className, children }) => {
  const [val, setVal] = useState("");
  useEffect(() => { if (editing) setVal(row[field] || ""); }, [editing, row, field]);
  if (!editing) {
    return (
      <TableCell className={`${className || ""} cursor-text hover:bg-blue-50/60`} onDoubleClick={onStart}
        title="Double-click to edit" data-testid={`usa-pc-cell-${field}-${row.id}`}>
        {children}
      </TableCell>
    );
  }
  return (
    <TableCell className="p-1">
      <Input autoFocus value={val} className="h-7 text-xs" data-testid={`usa-pc-cell-input-${field}-${row.id}`}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => onSave(val)}
        onKeyDown={(e) => { if (e.key === "Enter") onSave(val); if (e.key === "Escape") onCancel(); }} />
    </TableCell>
  );
};

export default function UsaProductCodesPanel({ user }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [inheritHint, setInheritHint] = useState("");  // shows "Auto-filled from <BRAND>"

  const isAdmin = user?.role === "admin";
  const [editCell, setEditCell] = useState(null);
  const [dupPreview, setDupPreview] = useState(null);

  const saveCell = async (row, field, value) => {
    setEditCell(null);
    if (String(row[field] || "") === String(value).trim()) return;
    try {
      const r = await axios.patch(`${API}/usa-product-codes/${row.id}`, { field, value }, { headers: authHeader() });
      setEntries((prev) => prev.map((e) => (e.id === row.id ? { ...e, ...r.data } : e)));
      toast.success("Cell updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const fetchEntries = useCallback(async (query = "") => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/usa-product-codes`, {
        headers: authHeader(),
        params: { q: query, limit: 1000 },
      });
      setEntries(r.data.entries || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load product codes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // Debounced search
  useEffect(() => {
    const id = setTimeout(() => fetchEntries(q), 300);
    return () => clearTimeout(id);
  }, [q, fetchEntries]);

  const openCreate = () => { setDraft(emptyDraft()); setInheritHint(""); setEditorOpen(true); };
  const openEdit = (row) => {
    setDraft({ ...row, htsus_code: row.htsus_code || "" });
    setInheritHint("");
    setEditorOpen(true);
  };

  // Auto-inherit HS / HTSUS / Product Code when adding a NEW row and the
  // typed Generic Name matches an existing DB entry (USA v2.1 rule).
  const tryInherit = async (genericInput) => {
    if (draft.id) return;                                    // only on create
    const g = String(genericInput || "").trim();
    if (!g || g.length < 3) { setInheritHint(""); return; }
    try {
      const r = await axios.get(`${API}/usa-product-codes`, {
        headers: authHeader(),
        params: { q: g, limit: 25 },
      });
      const list = r.data.entries || [];
      const upper = g.toUpperCase();
      const match = list.find(e => String(e.generic_name || "").trim().toUpperCase() === upper);
      if (!match) { setInheritHint(""); return; }
      // Only fill blanks — never overwrite what the user already typed.
      setDraft(prev => ({
        ...prev,
        hs_code:     prev.hs_code     || match.hs_code     || "",
        htsus_code:  prev.htsus_code  || match.htsus_code  || "",
        product_code: prev.product_code || match.product_code || "",
      }));
      setInheritHint(`Auto-filled from existing brand "${match.brand_name || match.hs_description}"`);
    } catch { /* silent */ }
  };

  // Debounce inherit lookups so we don't spam the API while typing.
  useEffect(() => {
    if (!editorOpen || draft.id) return;
    const g = draft.generic_name || "";
    const t = setTimeout(() => tryInherit(g), 350);
    return () => clearTimeout(t);
  }, [draft.generic_name, editorOpen]);

  const saveDraft = async () => {
    const hs = (draft.hs_description || draft.brand_name || "").trim();
    if (!hs) { toast.error("HS Description or Brand Name is required"); return; }
    const code = (draft.product_code || "").trim();
    if (!code) { toast.error("Product Code is required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/usa-product-codes`, draft, { headers: authHeader() });
      toast.success(draft.id ? "Product code updated" : "Product code added");
      setEditorOpen(false);
      fetchEntries(q);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const deleteEntry = async (id) => {
    setConfirmDelete(null);
    try {
      await axios.delete(`${API}/usa-product-codes/${id}`, { headers: authHeader() });
      toast.success("Product code deleted");
      fetchEntries(q);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const postUpload = (mode) => {
    const fd = new FormData();
    fd.append("file", uploadFile);
    fd.append("mode", mode);
    return axios.post(`${API}/usa-product-codes/upload`, fd, {
      headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
    });
  };

  const runImport = async (mode) => {
    setDupPreview(null);
    setUploading(true);
    try {
      const r = await postUpload(mode);
      setUploadResult(r.data);
      toast.success(`Imported ${r.data.imported}, updated ${r.data.updated}, skipped ${r.data.skipped}`);
      setUploadFile(null);
      fetchEntries(q);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const uploadExcel = async () => {
    if (!uploadFile) return;
    setUploading(true); setUploadResult(null);
    try {
      const r = await postUpload("preview");
      setUploading(false);
      if (r.data.duplicate_count > 0) { setDupPreview(r.data); return; }
      runImport("overwrite");
    } catch (err) {
      setUploading(false);
      toast.error(err.response?.data?.detail || "Upload failed");
    }
  };

  const downloadTemplate = () => {
    // Minimal CSV template — the /upload endpoint parses xlsx headers by name.
    // v2.1: HS CODE stored as 6-digit master, HTSUS CODE optional 7–10 digits.
    const csv = "HS DESCRIPTION,PRODUCT CODE,HS CODE,HTSUS CODE,GENERIC NAME\nTIATAN 12.5 MG,USA-1234,300490,3004909900,TIANEPTINE\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "usa_product_codes_template.csv";
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="usa-product-codes-panel">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600" />
            USA Product Code Database
          </CardTitle>
          <CardDescription>
            Master list of USA customs product codes. Used automatically when generating
            USA Bulk Data to populate the <code>PRODUCT CODE</code> column of the
            SubPieces sheet.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Toolbar */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search brand / generic / HS code / product code…"
                className="pl-8"
                data-testid="usa-pc-search"
              />
            </div>
            <Button variant="outline" size="sm" onClick={() => fetchEntries(q)} data-testid="usa-pc-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button size="sm" onClick={openCreate} data-testid="usa-pc-add">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
            <Button variant="ghost" size="sm" onClick={downloadTemplate} data-testid="usa-pc-template">
              <Download className="w-4 h-4 mr-1" /> Template
            </Button>
          </div>

          {/* Bulk upload */}
          {(
            <div className={`border-2 border-dashed rounded-xl p-3 flex items-center gap-3 ${
              uploadFile ? "border-blue-300 bg-blue-50" : "border-slate-200"
            }`} data-testid="usa-pc-upload-block">
              <FileSpreadsheet className="w-5 h-5 text-blue-600" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">Bulk import from Excel</div>
                <div className="text-xs text-slate-500">
                  Columns: <code>HS DESCRIPTION</code>, <code>PRODUCT CODE</code>, <code>HS CODE</code> (6-digit master), optional <code>HTSUS CODE</code> (7–10) &amp; <code>GENERIC NAME</code>.
                </div>
              </div>
              {uploadFile ? (
                <>
                  <span className="text-xs truncate max-w-[160px]">{uploadFile.name}</span>
                  <Button size="sm" onClick={uploadExcel} disabled={uploading} data-testid="usa-pc-upload-go">
                    {uploading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <><Upload className="w-3 h-3 mr-1" /> Import</>}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setUploadFile(null)} data-testid="usa-pc-upload-clear">
                    <X className="w-3 h-3" />
                  </Button>
                </>
              ) : (
                <label className="cursor-pointer inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-medium hover:bg-slate-50">
                  <input type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="usa-pc-upload-input"
                    onChange={(e) => e.target.files[0] && setUploadFile(e.target.files[0])} />
                  <Upload className="w-3 h-3" /> Choose file
                </label>
              )}
            </div>
          )}
          {uploadResult && (
            <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2" data-testid="usa-pc-upload-result">
              <CheckCircle2 className="w-4 h-4 inline text-emerald-600 mr-1" />
              Imported <strong>{uploadResult.imported}</strong>, updated <strong>{uploadResult.updated}</strong>, skipped <strong>{uploadResult.skipped || 0}</strong> (total {uploadResult.total_processed})
            </div>
          )}

          {/* Table */}
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>HS Description (Brand)</TableHead>
                  <TableHead>Generic Name</TableHead>
                  <TableHead>Product Code</TableHead>
                  <TableHead>HS Code (6)</TableHead>
                  <TableHead>HTSUS Code</TableHead>
                  <TableHead className="w-28 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && entries.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">
                    <RefreshCw className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                  </TableCell></TableRow>
                ) : entries.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8" data-testid="usa-pc-empty">
                    No product codes yet. Click Add or upload an Excel to populate.
                  </TableCell></TableRow>
                ) : (
                  entries.map((row) => (
                    <TableRow key={row.id} data-testid={`usa-pc-row-${row.id}`}>
                      {EDITABLE.map((f) => (
                        <EditableCell key={f} row={row} field={f}
                          editing={editCell?.id === row.id && editCell?.field === f}
                          onStart={() => setEditCell({ id: row.id, field: f })}
                          onSave={(v) => saveCell(row, f, v)}
                          onCancel={() => setEditCell(null)}
                          className={f === "hs_description" ? "font-medium" : f === "generic_name" ? "text-slate-600" : "font-mono text-xs"}>
                          {(f === "hs_description" ? (row.hs_description || row.brand_name) : row[f]) || <span className="text-slate-400">—</span>}
                        </EditableCell>
                      ))}
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(row)} data-testid={`usa-pc-edit-${row.id}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        {isAdmin && (
                          <>
                            <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-800"
                              onClick={() => setConfirmDelete(row)} data-testid={`usa-pc-delete-${row.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <div className="text-xs text-slate-500" data-testid="usa-pc-count">
            {entries.length} entr{entries.length === 1 ? "y" : "ies"}
          </div>
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent data-testid="usa-pc-editor">
          <DialogHeader>
            <DialogTitle>{draft.id ? "Edit Product Code" : "Add Product Code"}</DialogTitle>
            <DialogDescription>Matched by HS Description (case-insensitive) during USA bulk-data generation.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">HS Description (Brand) *</Label>
              <Input value={draft.hs_description || ""} onChange={(e) => setDraft({ ...draft, hs_description: e.target.value })}
                placeholder="TIATAN 12.5 MG" data-testid="usa-pc-editor-hs-desc" />
            </div>
            <div>
              <Label className="text-xs">Product Code *</Label>
              <Input value={draft.product_code || ""} onChange={(e) => setDraft({ ...draft, product_code: e.target.value })}
                placeholder="USA-1234" data-testid="usa-pc-editor-code" />
            </div>
            <div>
              <Label className="text-xs">HS Code <span className="text-slate-400 font-normal">(first 6 digits stored)</span></Label>
              <Input value={draft.hs_code || ""}
                onChange={(e) => setDraft({ ...draft, hs_code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                placeholder="300490" maxLength={6}
                className="font-mono"
                data-testid="usa-pc-editor-hs-code" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">HTSUS Code <span className="text-slate-400 font-normal">(7–10 digits)</span></Label>
              <Input value={draft.htsus_code || ""}
                onChange={(e) => setDraft({ ...draft, htsus_code: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                placeholder="3004909900" maxLength={10}
                className="font-mono"
                data-testid="usa-pc-editor-htsus-code" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Generic Name</Label>
              <Input value={draft.generic_name || ""} onChange={(e) => setDraft({ ...draft, generic_name: e.target.value })}
                placeholder="Testosterone Enanthate" data-testid="usa-pc-editor-generic" />
              {inheritHint && !draft.id && (
                <div className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1" data-testid="usa-pc-editor-inherit-hint">
                  <CheckCircle2 className="w-3 h-3" /> {inheritHint}
                </div>
              )}
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Description</Label>
              <Input value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="Optional notes" data-testid="usa-pc-editor-desc" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={saveDraft} disabled={saving} data-testid="usa-pc-editor-save">
              {saving ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
              {draft.id ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate handling on re-upload */}
      <Dialog open={!!dupPreview} onOpenChange={(o) => !o && setDupPreview(null)}>
        <DialogContent className="max-w-2xl" data-testid="usa-pc-dup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-4 h-4" /> {dupPreview?.duplicate_count} duplicate entr{dupPreview?.duplicate_count === 1 ? "y" : "ies"} found
            </DialogTitle>
            <DialogDescription>
              {dupPreview?.new} new row(s) will be added. Choose what to do with rows whose HS Description already exists.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-64 overflow-auto border rounded text-xs">
            <table className="w-full">
              <thead className="bg-slate-50 sticky top-0"><tr>
                <th className="text-left p-2">HS Description</th><th className="text-left p-2">Existing Code</th><th className="text-left p-2">New Code</th>
              </tr></thead>
              <tbody>
                {(dupPreview?.duplicates || []).map((d) => (
                  <tr key={d.hs_description} className="border-t">
                    <td className="p-2 font-medium">{d.hs_description}</td>
                    <td className="p-2 font-mono">{d.existing_code || "—"}</td>
                    <td className={`p-2 font-mono ${d.existing_code !== d.new_code ? "text-amber-700 font-semibold" : ""}`}>{d.new_code}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDupPreview(null)} data-testid="usa-pc-dup-cancel">Cancel</Button>
            <Button variant="outline" onClick={() => runImport("skip")} data-testid="usa-pc-dup-skip">Skip duplicates</Button>
            <Button onClick={() => runImport("overwrite")} data-testid="usa-pc-dup-overwrite">Overwrite duplicates</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent data-testid="usa-pc-confirm-delete">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-4 h-4" /> Delete product code?
            </DialogTitle>
            <DialogDescription>
              <strong>{confirmDelete?.hs_description || confirmDelete?.brand_name}</strong> ({confirmDelete?.product_code}) will be removed permanently.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => deleteEntry(confirmDelete.id)} data-testid="usa-pc-confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
